import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getLatestVital,
  type LatestVitalReading,
} from '../lib/timelineQueries';
import type { VitalSigns } from '@/types/db';

interface MetricInfo {
  key: keyof VitalSigns;
  label: string;
  unit: string;
  format?: (vs: VitalSigns) => string;
  icon: JSX.Element;
}

const METRIC_PRIORITY: MetricInfo[] = [
  {
    key: 'heart_rate',
    label: 'Heart Rate',
    unit: 'bpm',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-3 w-3 shrink-0" aria-hidden="true">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
      </svg>
    ),
  },
  {
    key: 'systolic_bp',
    label: 'Blood Pressure',
    unit: 'mmHg',
    format: (vs) =>
      vs.systolic_bp != null && vs.diastolic_bp != null
        ? `${vs.systolic_bp}/${vs.diastolic_bp}`
        : String(vs.systolic_bp ?? ''),
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-3 w-3 shrink-0" aria-hidden="true">
        <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
      </svg>
    ),
  },
  {
    key: 'blood_sugar',
    label: 'Blood Sugar',
    unit: 'mg/dL',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-3 w-3 shrink-0" aria-hidden="true">
        <path d="M12 2C8 8 5 11 5 15a7 7 0 0 0 14 0C19 11 16 8 12 2z" />
      </svg>
    ),
  },
  {
    key: 'weight_kg',
    label: 'Weight',
    unit: 'kg',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-3 w-3 shrink-0" aria-hidden="true">
        <path d="M5 17a7 7 0 0 1 14 0" />
        <path d="M12 10v7" />
        <circle cx="12" cy="10" r="1.5" />
        <line x1="3" y1="21" x2="21" y2="21" />
      </svg>
    ),
  },
];

function pickMetric(vs: VitalSigns): MetricInfo | null {
  return METRIC_PRIORITY.find((m) => vs[m.key] != null) ?? null;
}

function formatRelativeDate(dateStr: string | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(date);
}

export function LatestVitalChip({
  profileId,
  onViewAll,
}: {
  profileId: string;
  onViewAll: () => void;
}) {
  const [latest, setLatest] = useState<LatestVitalReading | null | undefined>(
    undefined,
  );

  useEffect(() => {
    let cancelled = false;
    getLatestVital(profileId)
      .then((data) => { if (!cancelled) setLatest(data); })
      .catch((err) => {
        console.error('[vitals-chip] load failed', err);
        if (!cancelled) setLatest(null);
      });
    return () => { cancelled = true; };
  }, [profileId]);

  if (latest === undefined) {
    return <div className="h-full min-h-[56px] animate-pulse bg-canvas rounded-[14px]" />;
  }

  if (latest === null) {
    return (
      <Link
        to={`/profile/${profileId}/add?type=vitals`}
        className="flex flex-col justify-center items-center min-h-[56px] rounded-[14px] border border-dashed border-border text-ink-subtle hover:text-ink-muted hover:border-border-strong active:bg-canvas px-2 py-2 gap-0.5"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5 shrink-0" aria-hidden="true">
          <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
        </svg>
        <span className="text-[10px] font-semibold text-center leading-tight">Add vitals</span>
      </Link>
    );
  }

  const metric = pickMetric(latest.vital_signs);
  if (!metric) return null;

  const rawVal = latest.vital_signs[metric.key] as number;
  const displayVal = metric.format
    ? metric.format(latest.vital_signs)
    : String(rawVal);

  return (
    <button
      type="button"
      onClick={onViewAll}
      className="flex flex-col justify-between min-h-[56px] w-full bg-surface border border-border rounded-[14px] px-3 py-2 text-left hover:bg-canvas active:bg-canvas"
    >
      <div className="flex items-center gap-1 text-evt-vitals-ink">
        {metric.icon}
        <span className="text-[10px] font-semibold uppercase tracking-wide truncate">
          {metric.label}
        </span>
      </div>
      <div className="flex items-baseline gap-1">
        <span className="text-sm font-bold text-ink">{displayVal}</span>
        <span className="text-[10px] text-ink-muted">{metric.unit}</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-ink-subtle">
          {formatRelativeDate(latest.event_date)}
        </span>
        <span className="text-[10px] font-semibold text-accent">View all →</span>
      </div>
    </button>
  );
}

export function VitalsHistorySection({ profileId: _profileId }: { profileId: string }) {
  return null;
}
